import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { parseApiBlobError } from '../../utils/parseApiBlobError';

const DISPLAY_TZ = 'America/Chicago';

export interface ActivitySnapshotModalProps {
  open: boolean;
  onClose: () => void;
  snapshotId: string | null;
  capturedAt?: string | null;
}

const ActivitySnapshotModal: React.FC<ActivitySnapshotModalProps> = ({
  open,
  onClose,
  snapshotId,
  capturedAt,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const revokeUrl = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setImageUrl(null);
  };

  useEffect(() => {
    if (!open || !snapshotId) {
      revokeUrl();
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    revokeUrl();

    void (async () => {
      try {
        const response = await apiClient.monitoring.getActivitySnapshot(snapshotId);
        if (cancelled) return;
        const blob = response.data as Blob;
        const nextUrl = URL.createObjectURL(blob);
        urlRef.current = nextUrl;
        setImageUrl(nextUrl);
      } catch (err) {
        if (!cancelled) {
          setError(await parseApiBlobError(err, 'Failed to load snapshot'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snapshotId]);

  useEffect(() => () => revokeUrl(), []);

  const capturedLabel = capturedAt
    ? moment(capturedAt).tz(DISPLAY_TZ).format('MMM D, YYYY h:mm:ss A')
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" component="span">Action Evidence Snapshot</Typography>
          {capturedLabel && (
            <Typography variant="body2" color="text.secondary" display="block">
              Captured at {capturedLabel}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close snapshot">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : imageUrl ? (
          <Box
            sx={{
              bgcolor: '#111',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 240,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt="Historical action evidence snapshot"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No snapshot available.</Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Historical evidence — not a live screen view.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ActivitySnapshotModal;
